import React, { useEffect, useState } from "react";
import { FiX, FiCheck, FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { Prescription, ExerciseInPlan, Exercise } from "../../../../../types";
import exercicies from "../../../../../services/exercicies";
import assistant from "../../../../../services/assistant";
import { toast } from "react-toastify";
import { PrescriptionHeader } from "./PrescriptionHeader";
import { ExerciseEditorRow } from "./ExerciseEditorRow";
import { ExerciseViewRow } from "./ExerciseViewRow";

interface DiffPrescriptionItemProps {
  readonly prescription: Prescription;
  readonly originalPrescription?: Prescription;
  readonly index: number;
  readonly isEditing: boolean;
  readonly editedPrescription: Prescription;
  readonly professionalId: number;
  readonly userId?: number | string;
  readonly onEdit: () => void;
  readonly onSave: (prescription: Prescription) => void;
  readonly onCancel: () => void;
  readonly onRemove: () => void;
  readonly onPrescriptionChange: (prescription: Prescription) => void;
}

export function DiffPrescriptionItem({
  prescription,
  originalPrescription,
  index,
  isEditing,
  editedPrescription,
  professionalId,
  userId,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onPrescriptionChange,
}: DiffPrescriptionItemProps) {
  const [exercisesList, setExercisesList] = useState<Exercise[]>([]);

  const getExercisesList = async () => {
    try {
      const exercises = await exercicies.list_all(
        `${professionalId}`,
        1,
        100,
        "",
      );
      setExercisesList(exercises);
    } catch (error) {
      console.error("Erro ao buscar exercícios:", error);
      return [];
    }
  };

  useEffect(() => {
    getExercisesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId]);

  const handleNotRecommend = async (
    exerciseId: number,
    forAll: boolean = false,
  ) => {
    if (!exerciseId) return;

    // Se for para todos, não precisa de userId
    if (!forAll && !userId) {
      toast.error("ID do aluno não disponível");
      return;
    }

    // Confirmação antes de executar
    const confirmMessage = forAll
      ? "Tem certeza que deseja marcar este exercício como não recomendado para TODOS os seus alunos?\n\nEsta ação impedirá que a IA recomende este exercício em qualquer plano de treino futuro."
      : "Tem certeza que deseja marcar este exercício como não recomendado para este aluno?\n\nEsta ação impedirá que a IA recomende este exercício para este aluno em planos de treino futuros.";

    const confirmed = globalThis.confirm(confirmMessage);
    if (!confirmed) {
      return;
    }

    try {
      const result = await assistant.notRecommendExercise(
        exerciseId,
        userId,
        forAll,
      );
      if (result.success) {
        const message = forAll
          ? "Exercício marcado como não recomendado para todos os alunos"
          : "Exercício marcado como não recomendado para este aluno";
        toast.success(message);
      } else {
        toast.error(result.error || "Erro ao marcar exercício");
      }
    } catch (error) {
      console.error("Erro ao marcar exercício como não recomendado:", error);
      toast.error("Erro ao processar solicitação");
    }
  };

  const updateExercise = (
    exerIndex: number,
    field: keyof ExerciseInPlan,
    value: string | number,
  ) => {
    const newExercises = [...(editedPrescription.exercises || [])];
    const currentExercise = newExercises[exerIndex];

    newExercises[exerIndex] = {
      ...currentExercise,
      [field]: value,
    };

    // Sincronizar sets/series e reps/repetitions
    if (field === "sets") {
      newExercises[exerIndex].series = value;
    }
    if (field === "reps") {
      newExercises[exerIndex].repetitions = value;
    }

    onPrescriptionChange({
      ...editedPrescription,
      exercises: newExercises,
    });
  };

  const updateExerciseId = (exerIndex: number, id: number) => {
    const newExercises = [...(editedPrescription.exercises || [])];
    newExercises[exerIndex] = {
      ...newExercises[exerIndex],
      id: id,
    };
    onPrescriptionChange({
      ...editedPrescription,
      exercises: newExercises,
    });
  };

  const removeExercise = (exerIndex: number) => {
    const newExercises = [...(editedPrescription.exercises || [])];
    newExercises.splice(exerIndex, 1);
    onPrescriptionChange({
      ...editedPrescription,
      exercises: newExercises,
    });
  };

  const addExercise = () => {
    const newExercises = [...(editedPrescription.exercises || [])];
    newExercises.push({
      id: 0,
      name: "",
      sets: 3,
      series: 3,
      reps: 10,
      repetitions: 10,
      load: "",
      interval: 60,
      professional: 0,
    } as ExerciseInPlan);
    onPrescriptionChange({
      ...editedPrescription,
      exercises: newExercises,
    });
  };

  if (isEditing) {
    return (
      <div className="rounded-xl sm:rounded-2xl border-2 border-purple-300 bg-purple-50/70 p-4 sm:p-6 shadow-md dark:border-purple-700 dark:bg-purple-900/20">
        <PrescriptionHeader
          index={index}
          prescription={editedPrescription}
          originalPrescription={originalPrescription}
          isEditing={true}
          exercisesList={exercisesList}
          onNameChange={(name) =>
            onPrescriptionChange({
              ...editedPrescription,
              name,
            })
          }
        />

        {/* Exercícios em Edição */}
        <div className="space-y-3 sm:space-y-4">
          {(editedPrescription.exercises || []).length > 0 && (
            <div className="mb-2 sm:mb-3 hidden sm:grid sm:grid-cols-12 gap-2 sm:gap-3 px-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <div className="col-span-12 md:col-span-4">Exercício</div>
              <div className="col-span-3 text-center md:col-span-2">Séries</div>
              <div className="col-span-3 text-center md:col-span-2">Reps</div>
              <div className="col-span-3 text-center md:col-span-2">Carga</div>
              <div className="col-span-2 text-center md:col-span-1">Int.</div>
              <div className="col-span-1"></div>
            </div>
          )}

          {(editedPrescription.exercises || []).map((ex, exIndex) => {
            const exerciseKey = ex.id || ex.name || `exercise-edit-${exIndex}`;
            return (
              <ExerciseEditorRow
                key={exerciseKey}
                exercise={ex}
                exIndex={exIndex}
                exercisesList={exercisesList}
                onUpdate={updateExercise}
                onRemove={removeExercise}
                onUpdateWithId={updateExerciseId}
              />
            );
          })}

          {/* Botão Adicionar Exercício */}
          <button
            type="button"
            onClick={addExercise}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/50 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-purple-600 transition-all hover:border-purple-400 hover:bg-purple-50 dark:border-purple-800 dark:bg-purple-900/10 dark:text-purple-400 dark:hover:bg-purple-900/20"
          >
            <FiPlus size={14} className="sm:w-4 sm:h-4" /> Adicionar Exercício
          </button>
        </div>

        {/* Botões de Ação */}
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => onSave(editedPrescription)}
            className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-green-600 px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-white transition hover:bg-green-700 hover:scale-105"
            title="Salvar alterações"
            aria-label={`Salvar alteracoes do treino ${index + 1}`}
          >
            <FiCheck size={16} className="sm:w-[18px] sm:h-[18px]" />
            Salvar
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-stroke bg-white px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4"
            title="Cancelar edição"
            aria-label={`Cancelar edicao do treino ${index + 1}`}
          >
            <FiX size={16} className="sm:w-[18px] sm:h-[18px]" />
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  const exerciseCount = prescription.exercises?.length || 0;

  return (
    <div className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm transition-all hover:shadow-md dark:border-strokedark dark:bg-boxdark">
      <div className="relative mb-3 sm:mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 w-full sm:w-auto">
          <PrescriptionHeader
            index={index}
            prescription={prescription}
            originalPrescription={originalPrescription}
            isEditing={false}
            exercisesList={exercisesList}
            onNameChange={() => {}}
          />
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            onClick={onEdit}
            className="min-h-[44px] min-w-[44px] rounded-xl p-2 sm:p-3 text-gray-600 transition-all hover:bg-purple-50 hover:text-purple-600 dark:text-gray-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-400"
            title="Editar treino"
            aria-label={`Editar treino ${index + 1}`}
          >
            <FiEdit2 size={16} className="sm:w-[18px] sm:h-[18px]" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="min-h-[44px] min-w-[44px] rounded-xl p-2 sm:p-3 text-gray-600 transition-all hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
            title="Remover treino"
            aria-label={`Remover treino ${index + 1}`}
          >
            <FiTrash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
          </button>
        </div>
      </div>

      {/* Exercícios (Visualização Compacta com Diff) */}
      <div className="relative space-y-2 sm:space-y-2">
        {prescription.exercises?.map((ex, exIndex) => {
          // Tenta encontrar o exercício original correspondente
          // Primeiro por índice, depois por nome e ID
          let originalExercise: ExerciseInPlan | undefined;

          if (originalPrescription?.exercises) {
            // Tenta por índice primeiro
            if (originalPrescription.exercises[exIndex]) {
              originalExercise = originalPrescription.exercises[exIndex];
            } else {
              // Tenta por nome e ID
              originalExercise = originalPrescription.exercises.find(
                (origEx) =>
                  (origEx.name === ex.name && origEx.name) ||
                  (origEx.id && ex.id && origEx.id === ex.id),
              );
            }
          }

          const exerciseKey = ex.id || ex.name || `exercise-${exIndex}`;

          return (
            <ExerciseViewRow
              key={exerciseKey}
              exercise={ex}
              originalExercise={originalExercise}
              onNotRecommend={handleNotRecommend}
              showNotRecommendButton={!!ex.id}
              allowForAll={true}
            />
          );
        })}
        {exerciseCount === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 sm:p-4 text-center text-xs sm:text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
            Nenhum exercício adicionado
          </div>
        )}
        {/* Mostrar exercícios removidos do original */}
        {originalPrescription?.exercises &&
          (() => {
            const newExerciseNames = new Set(
              prescription.exercises?.map((ex) => ex.name).filter(Boolean) ||
                [],
            );
            const removedExercises = originalPrescription.exercises.filter(
              (origEx) => !newExerciseNames.has(origEx.name || ""),
            );

            return removedExercises.length > 0 ? (
              <div className="mt-2 sm:mt-3 space-y-2">
                <p className="text-xs font-semibold text-red-600 dark:text-red-400">
                  Exercícios removidos:
                </p>
                {removedExercises.map((removedEx) => {
                  const removedKey =
                    removedEx.id ||
                    removedEx.name ||
                    `removed-${removedEx.name}`;
                  return (
                    <div
                      key={removedKey}
                      className="rounded-xl border border-red-200 bg-red-50/50 p-2.5 sm:p-3 dark:border-red-800 dark:bg-red-900/10"
                    >
                      <div className="text-xs sm:text-sm text-gray-400 line-through dark:text-gray-500">
                        {removedEx.name || "Exercício sem nome"}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2 sm:gap-3 text-xs text-gray-400 dark:text-gray-500">
                        {removedEx.sets || removedEx.series ? (
                          <span>
                            Séries: {removedEx.sets || removedEx.series}
                          </span>
                        ) : null}
                        {removedEx.reps || removedEx.repetitions ? (
                          <span>
                            Reps: {removedEx.reps || removedEx.repetitions}
                          </span>
                        ) : null}
                        {removedEx.load ? (
                          <span>Carga: {removedEx.load}</span>
                        ) : null}
                        {removedEx.interval ? (
                          <span>Int: {removedEx.interval}s</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null;
          })()}
      </div>
    </div>
  );
}
